import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import bkstApi from "../../api";
import NumericTextField from "../../common/component/numeric/NumericTextField";

const ADD_REASONS = [
  {
    value: "production",
    label: "Production",
    description: "Record newly produced items to increase stock",
  },
  {
    value: "adjustment",
    label: "Adjustment",
    description: "Fix an incorrect entry by adding stock",
  },
];

const LOSS_REASONS = [
  {
    value: "spoilage",
    label: "Spoilage",
    description: "Product expired or went bad",
  },
  {
    value: "breakage",
    label: "Breakage",
    description: "Product was damaged or broken",
  },
  {
    value: "shrinkage",
    label: "Shrinkage",
    description: "Loss due to theft or unexplained shortage",
  },
  {
    value: "adjustment",
    label: "Adjustment",
    description: "User Error: deduct stock to fix an incorrect entry",
  },
];

export default function StockUpdateDialog({ open, product, action, onClose, onStockUpdate }) {
  const [quantity, setQuantity] = useState("");
  const [type, setType] = useState("");
  const [updating, setUpdating] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setQuantity("");
      if (action === "remove") {
        setType(LOSS_REASONS[0].value);
      } else {
        setType(ADD_REASONS[0].value);
      }
    }
  }, [open]);

  const handleSubmit = () => {
    const qty = parseInt(quantity);
    if (!qty || qty <= 0) return;

    setUpdating(true);

    const payload = { type, quantity: action === "add" ? qty : -qty };

    bkstApi
      .post(`/my/product/${product.id}/inventory`, payload)
      .then((res) => {
        // Calculate new stock based on action
        const currentStock = product.stock || 0;
        const newStock = action === "add" ? currentStock + qty : Math.max(0, currentStock - qty);
        onStockUpdate(product.id, newStock, { type, quantity: payload.quantity });
        onClose();
      })
      .catch((error) => {
        console.error("Error updating stock:", error);
      })
      .finally(() => {
        setUpdating(false);
      });

    localStorage.removeItem("products");
  };

  if (!product) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ position: "relative", pr: 6 }}>
        <Typography variant="h6">{action === "add" ? "Add Stock" : "Remove Stock"}</Typography>
        <Typography variant="body2" color="textSecondary">
          {product.title}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: "absolute", top: 8, right: 8 }}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {action === "add" && (
          <Box py={1}>
            <RadioGroup value={type} onChange={(e) => setType(e.target.value)}>
              {ADD_REASONS.map((item) => (
                <FormControlLabel
                  key={item.value}
                  value={item.value}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">{item.label}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {item.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 0.5 }}
                />
              ))}
            </RadioGroup>
          </Box>
        )}

        {action === "remove" && (
          <Box py={1}>
            <RadioGroup value={type} onChange={(e) => setType(e.target.value)}>
              {LOSS_REASONS.map((item) => (
                <FormControlLabel
                  key={item.value}
                  value={item.value}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">{item.label}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {item.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 0.5 }}
                />
              ))}
            </RadioGroup>
          </Box>
        )}

        <Box pt={1}>
          <NumericTextField
            fullWidth
            label="Quantity"
            allowNegative={false}
            decimalScale={0}
            value={quantity}
            onChange={(value) => setQuantity(value)}
            isAllowed={(values) => {
              if (action === "remove" && values.floatValue > (product.stock || 0)) {
                return false;
              }
              return true;
            }}
            helperText={
              action === "add"
                ? `Current Stock: ${product.stock || 0} → New Stock: ${(product.stock || 0) + (parseInt(quantity) || 0)}`
                : `Current Stock: ${product.stock || 0} → New Stock: ${Math.max(0, (product.stock || 0) - (parseInt(quantity) || 0))}. Max Qty Allowed: ${product.stock || 0}.`
            }
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button fullWidth variant="outlined" onClick={onClose} disabled={updating}>
          Cancel
        </Button>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            updating ||
            !quantity ||
            parseInt(quantity) <= 0 ||
            (action === "remove" && parseInt(quantity) > (product.stock || 0))
          }
        >
          {updating ? "Updating..." : action === "add" ? "Add Stock" : "Remove Stock"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
